import FormText from 'components/form/FormText';
import { DivConst } from 'constants/divConst';

import type { UseFieldArrayReturn } from 'react-hook-form';
import { get, useFormContext } from 'react-hook-form';
import type { VoiceChatEditFormStateType } from 'types/page/voiceChatEditFormStateType';

import { useEffect, type JSX } from 'react';

/**
 * ボイスチャット作成編集 設問設定コンポーネント（モック 800-939 行相当）。
 */
export default function WsVoiceChatEditQuestions(props: {
  readOnly: boolean;
  fieldArray: UseFieldArrayReturn<VoiceChatEditFormStateType, 'questions', 'id'>;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  const {
    register,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useFormContext<VoiceChatEditFormStateType>();

  const { append, move, remove, fields } = props.fieldArray;

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 設問数が上限以下に戻ったら、件数超過エラー（配列ルートの custom エラー）だけをクリアする。
   * type === 'custom' 限定にしないと、設問個別の必須エラー（questions.N.title）まで一緒に消えてしまう。
   */
  useEffect(() => {
    if (
      fields.length <= DivConst.items.questionCountLimit.voiceChat &&
      get(errors, 'questions')?.type === 'custom'
    ) {
      clearErrors('questions');
    }
  }, [fields.length, errors, clearErrors]);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 設問の順番を入れ替えます。
   */
  const onMove = (questionIndex: number, upper: boolean) => {
    if (upper && questionIndex === 0) {
      return;
    }
    if (!upper && questionIndex === fields.length - 1) {
      return;
    }
    move(questionIndex, upper ? questionIndex - 1 : questionIndex + 1);
  };

  /**
   * 設問を削除します。
   */
  const onRemove = (questionIndex: number) => {
    if (fields.length <= 1) {
      return;
    }
    remove(questionIndex);
  };

  /**
   * 設問を追加します。
   */
  const onAppend = () => {
    if (fields.length >= DivConst.items.questionCountLimit.voiceChat) {
      return;
    }
    append({
      answerModeDiv: DivConst.items.voiceChatQuestionMode.explainAndAnswer.value,
      title: '',
      speechText: '',
      requiredFlag: true,
      supplementFlag: false,
      supplementPlaceholder: '補足があれば入力（任意）',
      titleDisplayFlag: true,
    });
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <section className="section-form-default section-question">
      <div className="container">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h3 className="title-section title-md mb-0">設問設定</h3>
          <span className="vc-hint">
            最大{DivConst.items.questionCountLimit.voiceChat}問 ／ 現在 {fields.length} 問
          </span>
        </div>

        {get(errors, 'questions')?.type === 'custom' && (
          <p className="error">{get(errors, 'questions')?.message}</p>
        )}

        {fields.map((field, index) => {
          const answerModeDiv = Number(
            watch(`questions.${index}.answerModeDiv`) ??
              DivConst.items.voiceChatQuestionMode.explainAndAnswer.value
          );
          const isExplainAndAnswer =
            answerModeDiv === DivConst.items.voiceChatQuestionMode.explainAndAnswer.value;
          const supplementFlag = watch(`questions.${index}.supplementFlag`);

          return (
            <div key={field.id} className="vc-qcard">
              <div className="vc-qcard-head">
                <span className="vc-qno">
                  <span className="num">{index + 1}</span>設問{index + 1}
                </span>
                <div className="vc-qtools">
                  <button type="button" onClick={() => onMove(index, true)} disabled={index === 0}>
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => onMove(index, false)}
                    disabled={index === fields.length - 1}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemove(index)}
                    disabled={fields.length <= 1}
                  >
                    🗑
                  </button>
                </div>
              </div>

              {/* 回答モード */}
              <div className="form-group">
                <label className="form-label">回答モード</label>
                <div className="vc-modes">
                  <div
                    className={`vc-mode${isExplainAndAnswer ? ' sel' : ''}`}
                    onClick={() =>
                      setValue(
                        `questions.${index}.answerModeDiv`,
                        DivConst.items.voiceChatQuestionMode.explainAndAnswer.value
                      )
                    }
                  >
                    <span className="vc-mode-ico">🎤</span>
                    <div className="vc-mode-txt">
                      <div className="mt">説明＋回答</div>
                      <div className="md">説明を読み上げてから、音声で回答を録音します</div>
                    </div>
                    <span className="vc-mode-check">{isExplainAndAnswer ? '✓' : ''}</span>
                  </div>
                  <div
                    className={`vc-mode${!isExplainAndAnswer ? ' sel' : ''}`}
                    onClick={() =>
                      setValue(
                        `questions.${index}.answerModeDiv`,
                        DivConst.items.voiceChatQuestionMode.explainOnly.value
                      )
                    }
                  >
                    <span className="vc-mode-ico">📢</span>
                    <div className="vc-mode-txt">
                      <div className="mt">説明のみ</div>
                      <div className="md">読み上げのみ。録音せず次の設問へ進みます</div>
                    </div>
                    <span className="vc-mode-check">{!isExplainAndAnswer ? '✓' : ''}</span>
                  </div>
                </div>
              </div>

              {/* 設問（説明文言） */}
              <div className="form-group">
                <FormText
                  subject="設問の説明文"
                  name={`questions.${index}.title`}
                  placeholder="読み上げる説明文を入力"
                  register={register}
                  errors={errors}
                  required={true}
                  requiredMark={true}
                  readOnly={props.readOnly}
                />
                <p className="vc-hint" style={{ marginTop: 6 }}>
                  🔊 画面に表示する文章です。
                </p>
              </div>

              {/* 読み上げ用テキスト（任意・回答モードによらず常に表示） */}
              <div className="form-group">
                <FormText
                  subject="読み上げ用テキスト（任意）"
                  name={`questions.${index}.speechText`}
                  placeholder="誤読する漢字をひらがなにして入力"
                  register={register}
                  errors={errors}
                  readOnly={props.readOnly}
                />
                <p className="vc-hint" style={{ marginTop: 6 }}>
                  空のときは説明文をそのまま読み上げます。画面の表示は説明文のままです。
                </p>
              </div>

              {/* 「説明＋回答」のときだけ 回答系の設定を出す */}
              {isExplainAndAnswer && (
                <>
                  <div className="form-group">
                    <div className="form-check">
                      <input
                        id={`voiceChatQuestionRequired-${index}`}
                        type="checkbox"
                        className="form-check-input"
                        disabled={props.readOnly}
                        {...register(`questions.${index}.requiredFlag`)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`voiceChatQuestionRequired-${index}`}
                      >
                        回答を必須にする
                      </label>
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="form-check">
                      <input
                        id={`voiceChatQuestionSupplement-${index}`}
                        type="checkbox"
                        className="form-check-input"
                        disabled={props.readOnly}
                        {...register(`questions.${index}.supplementFlag`)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`voiceChatQuestionSupplement-${index}`}
                      >
                        補足テキストの入力欄を表示する
                      </label>
                    </div>
                    {supplementFlag && (
                      <FormText
                        name={`questions.${index}.supplementPlaceholder`}
                        placeholder="補足入力欄のプレースホルダ"
                        register={register}
                        errors={errors}
                        readOnly={props.readOnly}
                      />
                    )}
                  </div>
                </>
              )}
              {/* 説明文表示フラグ */}
              <div className="form-group">
                <div className="form-check">
                  <input
                    id={`voiceChatQuestionTitleDisplay-${index}`}
                    type="checkbox"
                    className="form-check-input"
                    disabled={props.readOnly}
                    {...register(`questions.${index}.titleDisplayFlag`)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`voiceChatQuestionTitleDisplay-${index}`}
                  >
                    説明文を画面に表示する
                  </label>
                </div>
              </div>
              {!isExplainAndAnswer && (
                <p className="vc-hint">
                  読み上げ後、自動で次の設問へ進みます。導入や区切りの案内に使えます。
                </p>
              )}
            </div>
          );
        })}

        <div className="btn-container max-width-238 mx-auto">
          <button
            type="button"
            onClick={onAppend}
            disabled={
              props.readOnly || fields.length >= DivConst.items.questionCountLimit.voiceChat
            }
            className="btn btn-common moderate-blue w-100"
          >
            ＋ 設問を追加
          </button>
        </div>
      </div>
    </section>
  );
}
